/**
 * ContactsTab Component
 * Displays and manages contacts for an organization
 */
import React, { memo } from 'react';
import { Button } from '../ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Mail, 
  Phone,
  Smartphone,
  Users, 
  Star, 
  Plus, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertTriangle 
} from 'lucide-react';

export const ContactsTab = memo(({
  contacts,
  openContactDialog,
  openEditContactDialog,
  setAsPrimary,
  handleDeleteContact,
  onSendEmailToContact,
}) => {
  // Check if we need to prompt for primary contact selection
  const hasPrimaryContact = contacts?.some(c => c.is_primary);
  const needsPrimarySelection = contacts?.length > 1 && !hasPrimaryContact;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Contacts</h4>
        <Button size="sm" onClick={() => openContactDialog()}>
          <Plus className="w-3 h-3 mr-1" />
          Add Contact
        </Button>
      </div>
      
      {/* Alert when multiple contacts but no primary selected */}
      {needsPrimarySelection && (
        <Alert variant="warning" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <strong>Please select a Primary Contact.</strong> Click the menu on any contact and choose "Set as Primary" to designate the main point of contact for this organisation.
          </AlertDescription>
        </Alert>
      )}
      
      {contacts?.length > 0 ? (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {contacts.map(contact => (
            <div 
              key={contact.contact_id} 
              className={`p-3 border rounded-lg flex items-start justify-between ${
                needsPrimarySelection ? 'border-amber-300 dark:border-amber-700' : ''
              } ${contact.is_primary ? 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-300' : ''}`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{contact.name}</p>
                  {contact.is_primary && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 text-xs rounded-full">
                      <Star className="w-3 h-3 fill-current" />
                      Primary
                    </span>
                  )}
                </div>
                {contact.role && <p className="text-sm text-muted-foreground">{contact.role}</p>}
                {contact.email && (
                  <p className="text-sm flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {contact.email}
                    {onSendEmailToContact && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 ml-2 text-xs"
                        onClick={() => onSendEmailToContact(contact)}
                        data-testid={`contact-send-email-quick-${contact.contact_id}`}
                      >
                        Send Email
                      </Button>
                    )}
                  </p>
                )}
                {contact.phone && (
                  <p className="text-sm flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {contact.phone}
                  </p>
                )}
                {contact.mobile && (
                  <p className="text-sm flex items-center gap-1">
                    <Smartphone className="w-3 h-3" />
                    {contact.mobile}
                  </p>
                )}
                {contact.notes_address && (
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">
                    {contact.notes_address}
                  </p>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onSendEmailToContact && contact.email && (
                    <DropdownMenuItem
                      onClick={() => onSendEmailToContact(contact)}
                      data-testid={`contact-send-email-${contact.contact_id}`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => openEditContactDialog(contact)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  {!contact.is_primary && (
                    <DropdownMenuItem onClick={() => setAsPrimary(contact.contact_id)}>
                      <Star className="w-4 h-4 mr-2" />
                      Set as Primary
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleDeleteContact(contact.contact_id)} className="text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No contacts yet</p>
        </div>
      )}
    </div>
  );
});

ContactsTab.displayName = 'ContactsTab';

export default ContactsTab;
